import { apiClient } from './apiClient';

export interface PendingApprovalsSummary {
  total_pending: number;
  library_borrow_requests: number;
  library_return_confirmations: number;
  hr_department_approvals: number;
  hr_hr_approvals: number;
  hr_super_admin_approvals: number;
  sales_jobs_accounts_pending: number;
  sales_jobs_technical_pending: number;
  sales_site_visits_accounts_pending: number;
  sales_site_visits_technical_pending: number;
  sales_site_visits_final_approval_pending: number;
  vehicle_maintenance_pending: number;
  vehicle_requests_pending: number;
}

export const approvalsService = {
  async getPendingApprovalsSummary(): Promise<PendingApprovalsSummary> {
    try {
      // Fetch all pending approvals in parallel
      const [
        libraryBorrowRequests,
        libraryReturnConfirmations,
        hrDepartmentApprovals,
        hrHrApprovals,
        hrSuperAdminApprovals,
        salesJobsAccountsPending,
        salesJobsTechnicalPending,
        salesSiteVisitsAccountsPending,
        salesSiteVisitsTechnicalPending,
        salesSiteVisitsFinalApprovalPending,
        vehicleMaintenancePending,
        vehicleRequests,
      ] = await Promise.allSettled([
        apiClient.get('/library/borrow-requests/pending').catch(() => []),
        apiClient.get('/library/pending-return-confirmations/').catch(() => []),
        apiClient.get('/hr/leave-requests/pending-department').catch(() => []),
        apiClient.get('/hr/leave-requests/pending-hr').catch(() => []),
        apiClient.get('/hr/leave-requests/pending-super-admin').catch(() => []),
        apiClient.get('/sales/schedule-jobs/accounts/pending').catch(() => []),
        apiClient.get('/sales/schedule-jobs/technical/pending').catch(() => []),
        apiClient.get('/sales/site-visits/accounts/pending').catch(() => []),
        apiClient.get('/sales/site-visits/technical/pending').catch(() => []),
        apiClient.get('/sales/site-visits/final-approval/pending').catch(() => []),
        apiClient.get('/vehicle-management/maintenance').then(data => 
          Array.isArray(data) ? data.filter((item: any) => item.status === 'PENDING') : []
        ).catch(() => []),
        apiClient.get('/vehicles/requests/').catch(() => []),
      ]);

      // Helper function to get count from settled promise
      const getCount = (result: PromiseSettledResult<any>): number => {
        if (result.status === 'fulfilled' && Array.isArray(result.value)) {
          return result.value.length;
        }
        return 0;
      };

      // Filter vehicle requests for pending approval status
      let vehicleRequestsPendingCount = 0;
      if (vehicleRequests.status === 'fulfilled' && Array.isArray(vehicleRequests.value)) {
        vehicleRequestsPendingCount = vehicleRequests.value.filter(
          (request: any) => request.status === 'PENDING_APPROVAL'
        ).length;
      }

      const summary = {
        library_borrow_requests: getCount(libraryBorrowRequests),
        library_return_confirmations: getCount(libraryReturnConfirmations),
        hr_department_approvals: getCount(hrDepartmentApprovals),
        hr_hr_approvals: getCount(hrHrApprovals),
        hr_super_admin_approvals: getCount(hrSuperAdminApprovals),
        sales_jobs_accounts_pending: getCount(salesJobsAccountsPending),
        sales_jobs_technical_pending: getCount(salesJobsTechnicalPending),
        sales_site_visits_accounts_pending: getCount(salesSiteVisitsAccountsPending),
        sales_site_visits_technical_pending: getCount(salesSiteVisitsTechnicalPending),
        sales_site_visits_final_approval_pending: getCount(salesSiteVisitsFinalApprovalPending),
        vehicle_maintenance_pending: getCount(vehicleMaintenancePending),
        vehicle_requests_pending: vehicleRequestsPendingCount,
        total_pending: 0, // Will be calculated below
      };

      // Calculate total pending approvals
      summary.total_pending = Object.values(summary).reduce((sum, count) => {
        return typeof count === 'number' ? sum + count : sum;
      }, 0) - summary.total_pending; // Subtract total_pending since it's included in the sum

      return summary;
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.warn('Pending approvals requires authentication');
      } else if (error?.response?.status === 404) {
        console.warn('Some pending approval endpoints not found');
      } else {
        console.error('Failed to fetch pending approvals:', error?.message || error);
      }
      // Return empty summary on error
      return {
        total_pending: 0,
        library_borrow_requests: 0,
        library_return_confirmations: 0,
        hr_department_approvals: 0,
        hr_hr_approvals: 0,
        hr_super_admin_approvals: 0,
        sales_jobs_accounts_pending: 0,
        sales_jobs_technical_pending: 0,
        sales_site_visits_accounts_pending: 0,
        sales_site_visits_technical_pending: 0,
        sales_site_visits_final_approval_pending: 0,
        vehicle_maintenance_pending: 0,
        vehicle_requests_pending: 0,
      };
    }
  },
};
